import {
    applyCreationFieldsPatch,
    copyCreationFields,
    type WorkOrderCreationFields,
    type WorkOrderCreationFieldsPatch,
} from "../../../_shared/_logic/work-order-creation"
import type { AddressCandidate, BacklogListImport, LoadListOutput } from "../../../_shared/_logic/batch-upload-contract"
import type { PlanningUnitContactContext } from "../../../../../../../../../../../../../api/kehrblatt/planning"
import type { AddressTableFilters } from "../_shared/_logic/table-model"
import { candidateFromProperty, normalizeLoadListSources } from "../_shared/_logic/property-selection-selection"
import { BATCH_PLANNING_MAX_ROWS, normalizeSelectedBatchUnitIds } from "./planning-payloads"
import { backlogWorkSuggestions } from "./backlogWorkSuggestions"
import { estimateBacklogRow } from "../_shared/_logic/backlog-estimation"
import { summedTypicalDurationMinutes } from "../../../../../../../../../../../_shared/_logic/order-type-selection"

export type LoadListRowDraft = {
    id: string
    orderFields: WorkOrderCreationFields
    sourceCandidateKey: string
    propertyId: string | null
    selectedUnitId: string | null
    removed: boolean
    copyOriginRowId: string | null
    manualOrderTypes?: boolean
    manualDuration?: boolean
    workSourceCopied?: boolean
    workTargetSelectedManually?: boolean
    workWarnings?: string[]
    workEvidence?: string[]
    workTypesFromImport?: boolean
    workSourceRowIds?: string[]
}

type InitialState = {
    organizationId: string
    properties: Property[]
    output?: LoadListOutput
    createId?: () => string
}

/** Own the review draft for the overlay lifetime, independently of mounted table cells. */
export const createLoadListState = ({
    organizationId,
    properties,
    output,
    createId = () => crypto.randomUUID(),
}: InitialState) => {
    const outputProperties = output?.propertyPool ?? properties
    const propertyById = new Map(outputProperties.map((property) => [property.id, property]))
    const initialCandidates = (
        output
            ? Array.from(
                  new Set([
                      ...Object.keys(output.selectedUnitIdsByPropertyId),
                      ...output.selectedRows.map((row) => row.propertyId),
                  ])
              )
            : []
    ).flatMap((propertyId) => {
        const property = propertyById.get(propertyId)

        return property
            ? [
                  candidateFromProperty(
                      property,
                      normalizeLoadListSources(output?.selectionSourcesByPropertyId?.[propertyId], [
                          output?.importedFromList ? "backlog_pdf" : "direct",
                      ])
                  ),
              ]
            : []
    })

    const state = $state({
        creationDefaults: null as WorkOrderCreationFields | null,
        submissionPending: false,
        frozenSubmission: null as {
            requestId: string
            output: LoadListOutput
            payload: WorkOrderCreatePayload[]
        } | null,
        properties: outputProperties,
        candidates: initialCandidates as AddressCandidate[],
        rows: [] as LoadListRowDraft[],
        backlogListImport: output?.backlogListImport ?? (null as BacklogListImport | null),
        unitContactContexts: output?.unitContactContexts ?? ([] as PlanningUnitContactContext[]),
        filters: { search: "", status: "all", numberGreaterThan: "" } as AddressTableFilters,
    })
    let availableOrderTypes = $state.raw<OrderType[]>([])
    let defaultTypesAreManual = false
    let defaultDurationIsManual = false
    const sourcesById = $derived(
        new Map((state.backlogListImport?.rows ?? []).map((source) => [source.source_row_id, source]))
    )
    const estimateFor = (
        row: LoadListRowDraft,
        sources = (row.workSourceRowIds ?? []).flatMap((id) => {
            const source = sourcesById.get(id)
            return source ? [source] : []
        })
    ) =>
        estimateBacklogRow({
            sources: row.propertyId && row.selectedUnitId && !row.workSourceCopied ? sources : [],
            sourceSystem: state.backlogListImport?.sourceSystem,
            sourceColumns: state.backlogListImport?.sourceColumns ?? [],
            selectedIds: row.orderFields.order_type_ids,
            customType: row.orderFields.custom_order_type,
            orderTypes: availableOrderTypes,
            options: state.backlogListImport?.options,
        })
    const sameIds = (left: string[], right: string[]) =>
        left.length === right.length && left.every((id, index) => right[index] === id)
    const refreshWorkSuggestions = () => {
        if (state.frozenSubmission) return

        const suggestions = backlogWorkSuggestions(
            state.backlogListImport,
            state.candidates,
            state.rows,
            state.properties
        )
        for (const row of state.rows) {
            const suggestion = suggestions.get(row.id)
            row.workWarnings = suggestion?.warnings ?? []
            row.workSourceRowIds = suggestion?.sourceRowIds ?? []
            row.workEvidence = suggestion?.evidence ?? []

            if (output) {
                const estimate = estimateFor(row, suggestion?.sources ?? [])
                if (estimate.warning) row.workWarnings.push(estimate.warning)
                continue
            }

            if (!suggestion) {
                if (row.workTypesFromImport && !row.manualOrderTypes) {
                    row.orderFields = { ...row.orderFields, order_type_ids: [], custom_order_type: null }

                    if (!row.manualDuration) row.orderFields.estimated_duration_min = ""
                }

                row.workTypesFromImport = false
                continue
            }

            if (row.manualOrderTypes) {
                if (!sameIds(row.orderFields.order_type_ids, suggestion.ids) || row.orderFields.custom_order_type)
                    row.workWarnings.push("Ihre manuelle Auswahl weicht vom aktuellen Listenvorschlag ab.")

                row.workTypesFromImport = false
            } else {
                row.orderFields = {
                    ...row.orderFields,
                    order_type_ids: row.propertyId && row.selectedUnitId ? [...suggestion.ids] : [],
                    custom_order_type: null,
                }
                row.workTypesFromImport = Boolean(row.propertyId && row.selectedUnitId)
            }

            const estimate = estimateFor(row, suggestion.sources)
            if (estimate.warning) row.workWarnings.push(estimate.warning)
            if (!row.manualDuration) {
                row.orderFields.estimated_duration_min = String(estimate.durationMinutes ?? "")
            }
        }
    }

    const makeRow = (candidate: AddressCandidate, selectedUnitId: string | null): LoadListRowDraft => ({
        id: createId(),
        orderFields: copyCreationFields(
            state.creationDefaults ?? {
                order_type_ids: [],
                custom_order_type: null,
                estimated_duration_min: "",
                due_date: "",
                allowed_employee_ids: [],
            }
        ),
        sourceCandidateKey: candidate.key,
        propertyId: candidate.propertyId,
        selectedUnitId,
        removed: false,
        copyOriginRowId: null,
        manualOrderTypes: defaultTypesAreManual,
        manualDuration: defaultDurationIsManual,
    })

    const selectionFor = (
        candidate: AddressCandidate,
        selections: Record<string, string[]>,
        propertiesById: Map<string, Property>
    ) => {
        const property = propertiesById.get(candidate.propertyId ?? "")

        return property ? normalizeSelectedBatchUnitIds(property, selections[property.id]) : []
    }

    state.rows = output
        ? output.selectedRows.flatMap((row) => {
              const candidate = initialCandidates.find((item) => item.propertyId === row.propertyId)

              return candidate
                  ? [
                        {
                            ...makeRow(candidate, row.propertyUnitId),
                            id: row.rowId,
                            orderFields: row.orderFields
                                ? copyCreationFields(row.orderFields)
                                : makeRow(candidate, row.propertyUnitId).orderFields,
                            manualOrderTypes: row.planningOrigin?.manualOrderTypes,
                            manualDuration: row.planningOrigin?.manualDuration,
                            workTypesFromImport: row.planningOrigin?.workTypesFromImport,
                            workSourceCopied: row.planningOrigin?.workSourceCopied,
                            workSourceRowIds: row.planningOrigin?.workSourceRowIds,
                            workTargetSelectedManually: row.planningOrigin?.workTargetSelectedManually,
                        },
                    ]
                  : []
          })
        : initialCandidates.flatMap((candidate) => {
              const ids = selectionFor(candidate, output?.selectedUnitIdsByPropertyId ?? {}, propertyById)

              return (ids.length ? ids : [null]).map((id) => makeRow(candidate, id))
          })

    const selectedUnitIdsByPropertyId = () => {
        const selections: Record<string, string[]> = {}
        for (const row of state.rows) {
            if (row.removed || !row.propertyId) continue

            selections[row.propertyId] ??= []

            if (row.selectedUnitId && !selections[row.propertyId].includes(row.selectedUnitId)) {
                selections[row.propertyId].push(row.selectedUnitId)
            }
        }

        return selections
    }

    /** Preserve existing edits while reconciling source identities, never array positions. */
    const reconcileCandidates = (candidates: AddressCandidate[], initialSelections: Record<string, string[]> = {}) => {
        // Address keys can collide after manually reassigning a row; they are not draft identities.
        const seenKeys = new Set<string>()
        candidates = candidates.map((candidate) => {
            let key = candidate.key
            let suffix = 0
            while (seenKeys.has(key)) {
                key = `${candidate.key}:selection:${candidate.propertyId ?? "unresolved"}:${suffix++}`
            }
            seenKeys.add(key)

            return key === candidate.key ? candidate : { ...candidate, key }
        })
        const oldByKey = new Map(state.candidates.map((candidate) => [candidate.key, candidate]))
        const propertiesById = new Map(state.properties.map((property) => [property.id, property]))
        const nextKeys = new Set(candidates.map((candidate) => candidate.key))
        const candidatesByProperty = new Map(
            candidates.filter((candidate) => candidate.propertyId).map((candidate) => [candidate.propertyId, candidate])
        )
        const replacementFor = (row: LoadListRowDraft) =>
            candidatesByProperty.get(oldByKey.get(row.sourceCandidateKey)?.propertyId ?? null) ??
            candidatesByProperty.get(row.propertyId)
        const nextRows = state.rows.filter(
            (row) => nextKeys.has(row.sourceCandidateKey) || Boolean(replacementFor(row))
        )
        for (const row of nextRows) {
            if (!nextKeys.has(row.sourceCandidateKey)) {
                row.sourceCandidateKey = replacementFor(row)!.key
            }
        }
        const rowKeys = new Set(nextRows.map((row) => row.sourceCandidateKey))
        const propertiesWithRows = new Set(nextRows.map((row) => row.propertyId))
        const occupied = new Set(
            nextRows
                .filter((row) => !row.removed && row.selectedUnitId)
                .map((row) => `${row.propertyId}:${row.selectedUnitId}`)
        )
        for (const candidate of candidates) {
            if (rowKeys.has(candidate.key)) {
                const previous = oldByKey.get(candidate.key)

                if (!previous?.propertyId && candidate.propertyId) {
                    for (const row of nextRows.filter((item) => item.sourceCandidateKey === candidate.key)) {
                        if (!row.propertyId) {
                            row.propertyId = candidate.propertyId
                            propertiesWithRows.add(candidate.propertyId)
                            const ids = selectionFor(candidate, initialSelections, propertiesById).filter(
                                (id) => !occupied.has(`${candidate.propertyId}:${id}`)
                            )

                            if (ids.length === 1) {
                                row.selectedUnitId = ids[0]

                                if (!row.removed) occupied.add(`${candidate.propertyId}:${ids[0]}`)
                            }
                        }
                    }
                }

                continue
            }

            const ids = selectionFor(candidate, initialSelections, propertiesById).filter(
                (id) => !occupied.has(`${candidate.propertyId}:${id}`)
            )

            if (!ids.length && candidate.propertyId && propertiesWithRows.has(candidate.propertyId)) continue

            for (const id of ids.length ? ids : [null]) {
                nextRows.push(makeRow(candidate, id))
                propertiesWithRows.add(candidate.propertyId)

                if (id) occupied.add(`${candidate.propertyId}:${id}`)
            }
        }
        state.candidates = candidates
        state.rows = nextRows
        refreshWorkSuggestions()
    }

    /** Apply the backlog merge in linear passes, retaining row identities and empty copies. */
    const applyUnitSelections = (selections: Record<string, string[]>, propertyIds: string[]) => {
        const propertiesById = new Map(state.properties.map((property) => [property.id, property]))
        const candidatesByProperty = new Map(state.candidates.map((candidate) => [candidate.propertyId, candidate]))
        const groups = new Map<string, LoadListRowDraft[]>()
        for (const row of state.rows) {
            if (!row.propertyId) continue

            const group = groups.get(row.propertyId) ?? []
            group.push(row)
            groups.set(row.propertyId, group)
        }
        const discarded = new Set<string>()
        const additions = new Map<string | null, LoadListRowDraft[]>()
        for (const propertyId of new Set(propertyIds)) {
            const property = propertiesById.get(propertyId)
            const candidate = candidatesByProperty.get(propertyId)

            if (!property || !candidate) continue

            const allRows = groups.get(propertyId) ?? []
            const group = allRows.filter((row) => !row.removed)
            const requested = normalizeSelectedBatchUnitIds(property, selections[propertyId] ?? [])
            const wanted = new Set(requested)
            const kept = new Set<string>()
            for (const row of group) {
                if (!row.selectedUnitId) continue

                if (wanted.has(row.selectedUnitId) && !kept.has(row.selectedUnitId)) kept.add(row.selectedUnitId)
                else discarded.add(row.id)
            }
            const newRows: LoadListRowDraft[] = []
            const emptyRows = group.filter((row) => !row.selectedUnitId && !row.copyOriginRowId)
            let emptyIndex = 0
            for (const unitId of requested) {
                if (kept.has(unitId)) continue

                const empty = emptyRows[emptyIndex++]

                if (empty) empty.selectedUnitId = unitId
                else newRows.push(makeRow(candidate, unitId))
            }

            if (!group.some((row) => !discarded.has(row.id)) && !newRows.length) {
                newRows.push(makeRow(candidate, null))
            }

            if (newRows.length) {
                const anchorId = allRows.at(-1)?.id ?? null
                additions.set(anchorId, [...(additions.get(anchorId) ?? []), ...newRows])
            }
        }
        state.rows = [
            ...state.rows.flatMap((row) => [
                ...(!discarded.has(row.id) ? [row] : []),
                ...(additions.get(row.id) ?? []),
            ]),
            ...(additions.get(null) ?? []),
        ]
        refreshWorkSuggestions()
    }

    const selectUnit = (rowId: string, unitId: string | null): string | null => {
        const row = state.rows.find((item) => item.id === rowId)

        if (!row || row.removed) return "Diese Zeile kann nicht bearbeitet werden."

        const property = state.properties.find((item) => item.id === row.propertyId)

        if (unitId && !property?.units?.some((unit) => String(unit.id) === unitId)) {
            return "Die Einheit gehört nicht zur ausgewählten Liegenschaft."
        }

        const occupiedIndex = state.rows.findIndex(
            (item) =>
                !item.removed &&
                item.id !== rowId &&
                item.propertyId === row.propertyId &&
                item.selectedUnitId === unitId
        )

        if (unitId && occupiedIndex >= 0) {
            return `Diese Einheit ist bereits in Zeile ${occupiedIndex + 1} ausgewählt.`
        }

        row.selectedUnitId = unitId
        row.workTargetSelectedManually = Boolean(unitId)
        refreshWorkSuggestions()

        return null
    }

    const copyRow = (rowId: string, descending = false): string | null => {
        const index = state.rows.findIndex((item) => item.id === rowId)
        const row = state.rows[index]
        const property = state.properties.find((item) => item.id === row?.propertyId)
        const occupied = new Set(
            state.rows
                .filter((item) => !item.removed && item.propertyId === row?.propertyId)
                .map((item) => item.selectedUnitId)
        )

        if (
            !row ||
            row.removed ||
            !row.selectedUnitId ||
            !property ||
            !property.units?.some((unit) => !occupied.has(String(unit.id)))
        ) {
            return "Für diese Zeile ist keine weitere Einheit verfügbar."
        }

        if (state.rows.filter((item) => !item.removed).length >= BATCH_PLANNING_MAX_ROWS) {
            return "Pro Durchgang sind maximal 10.000 Planungszeilen möglich."
        }

        const copy = {
            ...row,
            orderFields: copyCreationFields(row.orderFields),
            id: createId(),
            selectedUnitId: null,
            copyOriginRowId: row.id,
            manualOrderTypes: true,
            manualDuration: true,
            workSourceCopied: true,
            workTypesFromImport: false,
            workWarnings: [],
            workEvidence: [],
            workSourceRowIds: [],
        }
        state.rows = [
            ...state.rows.slice(0, index + (descending ? 0 : 1)),
            copy,
            ...state.rows.slice(index + (descending ? 0 : 1)),
        ]

        return null
    }

    const setRemoved = (rowIds: string[], removed: boolean): string[] => {
        const ids = new Set(rowIds)
        const conflicts: string[] = []
        const occupied = new Map(
            state.rows
                .filter((row) => !row.removed && !ids.has(row.id) && row.selectedUnitId)
                .map((row) => [`${row.propertyId}:${row.selectedUnitId}`, row.id])
        )
        for (const row of state.rows) {
            if (!ids.has(row.id) || row.removed === removed) continue

            row.removed = removed

            if (!removed && row.selectedUnitId) {
                const key = `${row.propertyId}:${row.selectedUnitId}`
                const conflictingId = occupied.get(key)

                if (conflictingId) {
                    const index = state.rows.findIndex((item) => item.id === conflictingId)
                    conflicts.push(
                        `Die frühere Einheit ist bereits in Zeile ${index + 1} ausgewählt. Bitte wählen Sie eine freie Einheit.`
                    )
                    row.selectedUnitId = null
                } else {
                    occupied.set(key, row.id)
                }
            }
        }

        refreshWorkSuggestions()

        return conflicts
    }

    const changeProperty = (rowId: string, property: Property | null, initialUnitIds: string[]) => {
        const row = state.rows.find((item) => item.id === rowId)

        if (!row || row.removed) return

        if (property) {
            state.properties = [...state.properties.filter((item) => item.id !== property.id), property]
        }

        row.propertyId = property?.id ?? null
        row.workTargetSelectedManually = false
        row.selectedUnitId = null
        row.copyOriginRowId = null

        if (initialUnitIds.length === 1) selectUnit(row.id, initialUnitIds[0])

        refreshWorkSuggestions()
    }

    return {
        estimatesByRowId: () => new Map(state.rows.map((row) => [row.id, estimateFor(row)])),
        estimateForRow: (rowId: string) => {
            const row = state.rows.find((item) => item.id === rowId)
            return row ? estimateFor(row) : null
        },
        useCalculatedDuration: (rowId: string) => {
            const row = state.rows.find((item) => item.id === rowId)
            if (!row || row.removed || state.frozenSubmission || output) return
            row.manualDuration = false
            refreshWorkSuggestions()
        },
        configureOrderTypes: (orderTypes: OrderType[]) => {
            availableOrderTypes = orderTypes
            refreshWorkSuggestions()
        },
        refreshWorkSuggestions,
        setBacklogListImport: (backlog: BacklogListImport) => {
            state.backlogListImport = backlog
            refreshWorkSuggestions()
        },
        initializeDefaults: (value: WorkOrderCreationFields) => {
            if (state.creationDefaults) return

            state.creationDefaults = copyCreationFields(value)

            if (!output) for (const row of state.rows) row.orderFields = copyCreationFields(value)
        },
        applyOrderFields: (patch: WorkOrderCreationFieldsPatch, selectedIds: string[]) => {
            if (state.frozenSubmission) return

            const selected = new Set(selectedIds)

            if (!selected.size && state.creationDefaults)
                state.creationDefaults = applyCreationFieldsPatch(state.creationDefaults, patch)

            if (!selected.size && patch.orderTypeSelection !== undefined) defaultTypesAreManual = true

            if (!selected.size && patch.estimated_duration_min !== undefined) defaultDurationIsManual = true

            for (const row of state.rows) {
                if (!row.removed && (!selected.size || selected.has(row.id))) {
                    row.orderFields = applyCreationFieldsPatch(row.orderFields, patch)

                    if (patch.orderTypeSelection !== undefined) row.manualOrderTypes = true

                    if (patch.estimated_duration_min !== undefined) row.manualDuration = true
                }
            }
            refreshWorkSuggestions()
        },
        saveOrderFields: (rowId: string, value: WorkOrderCreationFields): string | null => {
            const row = state.rows.find((row) => row.id === rowId)

            if (!row || row.removed || state.frozenSubmission) return "Diese Zeile kann nicht mehr bearbeitet werden."

            const typesChanged =
                !sameIds(row.orderFields.order_type_ids, value.order_type_ids) ||
                JSON.stringify(row.orderFields.custom_order_type) !== JSON.stringify(value.custom_order_type)

            if (typesChanged) row.manualOrderTypes = true

            const durationChanged = row.orderFields.estimated_duration_min !== value.estimated_duration_min
            const selectionDuration = String(
                summedTypicalDurationMinutes(value.order_type_ids, availableOrderTypes) ?? ""
            )

            if (durationChanged && !(typesChanged && value.estimated_duration_min === selectionDuration))
                row.manualDuration = true

            row.orderFields = copyCreationFields(
                typesChanged && row.manualDuration && value.estimated_duration_min === selectionDuration
                    ? { ...value, estimated_duration_min: row.orderFields.estimated_duration_min }
                    : value
            )
            refreshWorkSuggestions()

            return null
        },
        organizationId,
        state,
        selectedUnitIdsByPropertyId,
        reconcileCandidates,
        applyUnitSelections,
        selectUnit,
        copyRow,
        setRemoved,
        changeProperty,
    }
}

export type LoadListState = ReturnType<typeof createLoadListState>
